import {
  PCIFileData,
  PCIDatabaseData,
  PCISharedWithPartnersData,
  PCIAccessRelatedData,
  FilesWithPCIDataParams,
  DatabasesWithPCIDataParams,
  AccessRelatedDataForPCIParams,
  FileDistributionByPCILabelsParams,
  queryFilesWithPCIData,
  queryDatabasesWithPCIData,
  queryPCIDataSharedWithPartners,
  queryAccessRelatedDataForPCI,
  queryFileDistributionByPCILabels,
  mapQueryFilesWithPCIData,
  mapQueryDatabasesWithPCIData,
  mapQueryPCIDataSharedWithPartners,
  mapQueryAccessRelatedDataForPCI,
  mapQueryFileDistributionByPCILabels,
  PCIFileDistributionData,
  AccessControlDataParams,
  queryAccessControlData,
  mapQueryAccessControlData,
  PCIAccessControlData,
  mapQueryAttributeSetId,
  queryAttributeSetId,
  AttributeSetData
} from './queries'
import graphqlService from '../../../services/graphqlService'
import { UNIQUE_WIDGET_INDEX } from '../mainDashboardv2/constants'
import apiService from '../../../services/api/apiService'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export const ACTION_FETCH_PCI_FILES_DATA = 'complianceDashboard/fetchPCIFilesData'
export const fetchPCIFilesData = createAsyncThunk(
  ACTION_FETCH_PCI_FILES_DATA,
  async (params: FilesWithPCIDataParams & { [UNIQUE_WIDGET_INDEX]?: string }) => {
    const resultRaw = await graphqlService.execute(queryFilesWithPCIData(params))
    return mapQueryFilesWithPCIData(resultRaw)
  }
)

export const ACTION_FETCH_PCI_DATABASES_DATA = 'complianceDashboard/fetchPCIDatabasesData'
export const fetchPCIDatabasesData = createAsyncThunk(
  ACTION_FETCH_PCI_DATABASES_DATA,
  async (params: DatabasesWithPCIDataParams & { [UNIQUE_WIDGET_INDEX]?: string }) => {
    const resultRaw = await graphqlService.execute(queryDatabasesWithPCIData(params))
    return mapQueryDatabasesWithPCIData(resultRaw)
  }
)

export const ACTION_FETCH_PCI_SHARED_WITH_PARTNERS =
  'complianceDashboard/fetchPCISharedWithPartners'
export const fetchPCISharedWithPartners = createAsyncThunk(
  ACTION_FETCH_PCI_SHARED_WITH_PARTNERS,
  async (params: { attributeSetId: string; [UNIQUE_WIDGET_INDEX]?: string }) => {
    const resultRaw = await graphqlService.execute(queryPCIDataSharedWithPartners(params))
    return {
      data: mapQueryPCIDataSharedWithPartners(resultRaw),
      [UNIQUE_WIDGET_INDEX]: params[UNIQUE_WIDGET_INDEX]
    }
  }
)

export const ACTION_FETCH_PCI_ACCESS_RELATED_DATA = 'complianceDashboard/fetchPCIAccessRelatedData'
export const fetchPCIAccessRelatedData = createAsyncThunk(
  ACTION_FETCH_PCI_ACCESS_RELATED_DATA,
  async (params: AccessRelatedDataForPCIParams & { [UNIQUE_WIDGET_INDEX]?: string }) => {
    const resultRaw = await graphqlService.execute(queryAccessRelatedDataForPCI(params))
    return mapQueryAccessRelatedDataForPCI(resultRaw)
  }
)

export const ACTION_FETCH_PCI_FILE_DISTRIBUTION = 'complianceDashboard/fetchPCIFileDistribution'
export const fetchPCIFileDistribution = createAsyncThunk(
  ACTION_FETCH_PCI_FILE_DISTRIBUTION,
  async (params: FileDistributionByPCILabelsParams & { [UNIQUE_WIDGET_INDEX]?: string }) => {
    const resultRaw = await graphqlService.execute(queryFileDistributionByPCILabels(params))
    return mapQueryFileDistributionByPCILabels(resultRaw)
  }
)

const DISPLAY_TIME_RANGES = {
  LAST_3_MONTHS: '0_3_MONTHS',
  LAST_6_MONTHS: '0_6_MONTHS',
  LAST_1_YEAR: '0_1_YEARS',
  LAST_3_YEARS: '0_3_YEARS',
  MORE_THAN_3_YEARS: 'GT_3_YEARS'
} as const

export const transformFilesDistributionByTimeData = (data: any[], activeTab: string): any[] => {
  if (!data || !Array.isArray(data)) return []

  const categoryKey =
    activeTab === 'last_modified' ? 'lastModifiedDateCategory' : 'lastAccessTimeCategory'

  const dataMap = new Map<string, number>()
  data.forEach((item) => {
    dataMap.set(item[categoryKey], item.count)
  })

  const transformedData = Object.entries(DISPLAY_TIME_RANGES).map(([timeRange, backendRange]) => ({
    timeRange,
    count: dataMap.get(backendRange) || 0
  }))

  return transformedData
}

export const ACTION_FETCH_PCI_LAST_MODIFIED_DATA = 'complianceDashboard/fetchPCILastModifiedData'
export const fetchPCILastModifiedData = createAsyncThunk(
  ACTION_FETCH_PCI_LAST_MODIFIED_DATA,
  async () => {
    const result = await apiService.getComplianceLastModifiedData()
    return transformFilesDistributionByTimeData(result, 'last_modified')
  }
)

export const ACTION_FETCH_PCI_LAST_ACCESSED_DATA = 'complianceDashboard/fetchPCILastAccessedData'
export const fetchPCILastAccessedData = createAsyncThunk(
  ACTION_FETCH_PCI_LAST_ACCESSED_DATA,
  async () => {
    const result = await apiService.getComplianceLastAccessedData()
    return transformFilesDistributionByTimeData(result, 'last_accessed')
  }
)

export const ACTION_FETCH_PCI_ACCESS_CONTROL_DATA = 'complianceDashboard/fetchPCIAccessControlData'
export const fetchPCIAccessControlData = createAsyncThunk(
  ACTION_FETCH_PCI_ACCESS_CONTROL_DATA,
  async (params: AccessControlDataParams & { [UNIQUE_WIDGET_INDEX]?: string }) => {
    const resultRaw = await graphqlService.execute(queryAccessControlData(params))
    return mapQueryAccessControlData(resultRaw)
  }
)

export const ACTION_FETCH_ATTRIBUTE_SET_ID = 'complianceDashboard/fetchAttributeSetId'
export const fetchAttributeSetId = createAsyncThunk(
  ACTION_FETCH_ATTRIBUTE_SET_ID,
  async (attributeSetName: string) => {
    const resultRaw = await graphqlService.execute(queryAttributeSetId(attributeSetName))
    return mapQueryAttributeSetId(resultRaw)
  }
)

interface ComplianceDashboardState {
  pciFilesData?: { [key: string]: PCIFileData | null }
  pciDatabasesData?: { [key: string]: PCIDatabaseData | null }
  pciSharedWithPartnersData?: { [key: string]: PCISharedWithPartnersData | null }
  pciAccessRelatedData?: { [key: string]: PCIAccessRelatedData | null }
  pciFileDistribution?: { [key: string]: PCIFileDistributionData | null }
  pciLastModifiedData?: { [key: string]: any | null }
  pciLastAccessedData?: { [key: string]: any | null }
  pciAccessControlData?: { [key: string]: PCIAccessControlData | null }
  attributeSetData?: AttributeSetData | null
}

const initialState: ComplianceDashboardState = {
  pciFilesData: {},
  pciDatabasesData: {},
  pciSharedWithPartnersData: {},
  pciAccessRelatedData: {},
  pciFileDistribution: {},
  pciLastModifiedData: {},
  pciLastAccessedData: {},
  pciAccessControlData: {},
  attributeSetData: null
}

const complianceDashboardSlice = createSlice({
  name: 'complianceDashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPCIFilesData.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciFilesData = {
          ...state.pciFilesData,
          [widgetIndex]: payload
        }
      }
    })
    builder.addCase(fetchPCIDatabasesData.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciDatabasesData = {
          ...state.pciDatabasesData,
          [widgetIndex]: payload
        }
      }
    })
    builder.addCase(fetchPCISharedWithPartners.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciSharedWithPartnersData = {
          ...state.pciSharedWithPartnersData,
          [widgetIndex]: payload.data
        }
      }
    })
    builder.addCase(fetchPCIAccessRelatedData.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciAccessRelatedData = {
          ...state.pciAccessRelatedData,
          [widgetIndex]: payload
        }
      }
    })
    builder.addCase(fetchPCIFileDistribution.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciFileDistribution = {
          ...state.pciFileDistribution,
          [widgetIndex]: payload
        }
      }
    })
    builder.addCase(fetchPCILastModifiedData.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciLastModifiedData = {
          ...state.pciLastModifiedData,
          [widgetIndex]: payload
        }
      }
    })

    builder.addCase(fetchPCILastAccessedData.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciLastAccessedData = {
          ...state.pciLastAccessedData,
          [widgetIndex]: payload
        }
      }
    })
    builder.addCase(fetchPCIAccessControlData.fulfilled, (state, { payload, meta }) => {
      const widgetIndex = meta.arg[UNIQUE_WIDGET_INDEX]
      if (widgetIndex) {
        state.pciAccessControlData = {
          ...state.pciAccessControlData,
          [widgetIndex]: payload
        }
      }
    })
    builder.addCase(fetchAttributeSetId.fulfilled, (state, { payload }) => {
      state.attributeSetData = payload
    })
  }
})

export default complianceDashboardSlice.reducer
